/* eslint complexity: ["error", { max: 10 }] -- MultiSelectTextField has conditional logic for read-only mode and array/string value handling */
/* eslint max-lines-per-function: ["error", { max: 165 }] -- MultiSelectTextField includes read-only rendering and complex animation logic */
import { useEffect, useMemo, useState } from "react";

import isNil from "lodash/isNil";
import { AnimatePresence } from "tamagui";

import { InputFieldBaseStyles, TextInputBaseStyles } from "@/constants/UI";
import MultiSelectModal from "@/ui/composed/modal/MultiSelectModal/MultiSelectModal";
import Icon, { FAIconName } from "@/ui/core/Icon";
import Text from "@/ui/core/Text";
import TextTag from "@/ui/core/TextTag";
import { Box, HStack } from "@/ui/layout";
import { AnimationStyles } from "@/ui/theme/styles";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface MultiSelectTextFieldProps extends ViewWithTestID {
  defaultSelectedValues?: MultiSelectOption[];
  hasError?: boolean;
  isDisabled?: boolean;
  isSearchable?: boolean;
  isSingleSelect?: boolean;
  modalTitle: string;
  onPressIn?: () => void;
  onValuesChange: (selection: MultiSelectOption[]) => void;
  options: MultiSelectOption[];
  value?: null | string | string[];
}
const { fadeOut } = AnimationStyles;

interface CreateTagComponentProps extends ViewWithTestID {
  dismissedValueIndex?: number;
  index: number;
  isDisabled?: boolean;
  setDismissedValueIndex: (index?: number) => void;
  value: string;
}

const createTagComponent = ({
  dismissedValueIndex,
  index,
  isDisabled,
  setDismissedValueIndex,
  testID,
  value: v,
}: CreateTagComponentProps) => {
  const baseTestID = joinTestIDs(index.toString(), v);
  const tagTestID = testID
    ? joinTestIDs(testID, "tag", index.toString())
    : undefined;

  return (
    <AnimatePresence key={joinTestIDs(baseTestID, "animatePresence")}>
      <Box
        exitStyle={index === dismissedValueIndex ? fadeOut : null}
        key={joinTestIDs(baseTestID, "box")}
        transition={index === dismissedValueIndex ? "fadeSlow" : null}
      >
        <TextTag
          key={joinTestIDs(baseTestID, "textTag")}
          onDismissPress={() => !isDisabled && setDismissedValueIndex(index)}
          testID={tagTestID}
          text={v}
          variant="selection"
        />
      </Box>
    </AnimatePresence>
  );
};

const MultiSelectTextField = ({
  defaultSelectedValues,
  hasError,
  isDisabled,
  isSearchable,
  isSingleSelect,
  modalTitle,
  onPressIn,
  onValuesChange,
  options,
  testID,
  value,
}: MultiSelectTextFieldProps) => {
  const [isSelectionOpen, setIsSelectionOpen] = useState<boolean>(false);
  const [dismissedValueIndex, setDismissedValueIndex] = useState<
    number | undefined
  >(undefined);
  const [isDismissAnimationTriggered, setIsDismissAnimationTriggered] =
    useState<boolean>(false);

  const values = useMemo(() => {
    if (isNil(value) || !Array.isArray(value)) {
      return null;
    }

    // render a TextTag object for each provided value; with transition set to null for all TextTags initially
    // once a TextTag's dismiss button is pressed, re-render the TextTags so that the dismissed one gets transition="fadeSlow"
    // eslint-disable-next-line max-params
    const tagComponents = value.map((v, index) =>
      createTagComponent({
        dismissedValueIndex,
        index,
        isDisabled,
        setDismissedValueIndex,
        testID,
        value: v,
      }),
    );

    // if a TextTag has been dismissed, trigger the dismiss animation separately
    // so that this TextTag has first been re-rendered WITH a transition="fadeSlow", and is then removed afterwards
    // (otherwise, either no transition plays, or if transition="fadeSlow" for all TextTags, then simply
    //  the re-ordering of elements in the HStack post element removal will "fade out" ALL shuffled elements)
    if (!isNil(dismissedValueIndex)) {
      setIsDismissAnimationTriggered(true);
    }

    return tagComponents;
  }, [isDisabled, value, dismissedValueIndex, testID]);

  useEffect(() => {
    if (
      !isDismissAnimationTriggered ||
      isNil(dismissedValueIndex) ||
      !Array.isArray(value)
    ) {
      return;
    }

    // - remove the element to be dismissed from the values array
    // - said element will not be rendered in the above value.map() block
    // - on its un-rendering, the element's "fadeSlow" animation will be invoked
    onValuesChange(
      value
        // eslint-disable-next-line max-params
        .filter((_, index) => index !== dismissedValueIndex)
        .map((x) => ({ displayText: x, value: x })),
    );

    // reset everything back to a clean state
    setDismissedValueIndex(undefined);
    setIsDismissAnimationTriggered(false);
  }, [isDismissAnimationTriggered, dismissedValueIndex, onValuesChange, value]);

  return (
    <HStack
      // eslint-disable-next-line react/jsx-props-no-spreading -- spread these props FIRST, then override with following props as needed
      {...InputFieldBaseStyles}
      alignItems="center"
      backgroundColor={
        isDisabled
          ? ColorToken.NeutralBackgroundSecondary
          : ColorToken.NeutralBackgroundPrimary
      }
      borderColor={
        hasError
          ? ColorToken.NotificationCritical
          : InputFieldBaseStyles.borderColor
      }
      flexGrow={1}
      flexShrink={1}
      height="auto"
      justifyContent="space-between"
      minHeight={InputFieldBaseStyles.height}
      onPress={() => {
        if (isDisabled) return;
        onPressIn?.();
        setIsSelectionOpen(true);
      }}
      paddingHorizontal={12}
      paddingVertical={TextInputBaseStyles.paddingVertical}
    >
      {Array.isArray(value) ? (
        <HStack
          flex={1}
          flexWrap="wrap"
          gap={6}
          testID={joinTestIDs(testID, "existing")}
        >
          {values}
        </HStack>
      ) : (
        <Text testID={joinTestIDs(testID, "existing")} variant="body">
          {value}
        </Text>
      )}

      <HStack gap={6}>
        {!isDisabled && (
          <Box onPress={() => onValuesChange([])}>
            <Icon
              name={FAIconName.Times}
              testID={joinTestIDs(testID, "close")}
            />
          </Box>
        )}
        <Box>
          <Icon
            name={FAIconName.ChevronDown}
            testID={joinTestIDs(testID, "expand-options")}
          />
        </Box>
      </HStack>
      <MultiSelectModal
        defaultSelectedValues={defaultSelectedValues}
        isOpen={isSelectionOpen}
        isSearchable={isSearchable}
        isSingleSelect={isSingleSelect}
        isSmallBottomPadding
        onDismiss={() => setIsSelectionOpen(false)}
        onValuesChange={onValuesChange}
        options={options}
        testID={joinTestIDs(testID, "select")}
        title={modalTitle}
      />
    </HStack>
  );
};

export type { MultiSelectTextFieldProps };
export default MultiSelectTextField;
